/**
 * Vermittlungsprozess: Submissions, Interviews, Placements (Schritt 3).
 * Endpunkte unter /api/v1/submission-workflow.
 *
 * Grundsatz: Der Prozessstatus ist unabhängig vom Kandidatenstatus.
 */

import type { ProzessInterview } from '../utils/calendarMapping';

const API_BASE = '/api/v1/submission-workflow';

export type SubmissionStatus =
  | 'DRAFT' | 'SUBMITTED' | 'CLIENT_REVIEW' | 'INTERVIEW_REQUESTED'
  | 'INTERVIEW_SCHEDULED' | 'INTERVIEW_COMPLETED' | 'SECOND_INTERVIEW'
  | 'OFFER' | 'OFFER_ACCEPTED' | 'PLACED'
  | 'REJECTED_CLIENT' | 'REJECTED_CANDIDATE' | 'WITHDRAWN' | 'CLOSED';

export const SUBMISSION_STATUS_CONFIG: Record<SubmissionStatus, { label: string; color: string; bgColor: string }> = {
  DRAFT:               { label: 'Vorbereitung',          color: '#6B7280', bgColor: 'rgba(107,114,128,0.12)' },
  SUBMITTED:           { label: 'Vorgestellt',           color: '#3B82F6', bgColor: 'rgba(59,130,246,0.12)' },
  CLIENT_REVIEW:       { label: 'Kunde prüft',           color: '#8B5CF6', bgColor: 'rgba(139,92,246,0.12)' },
  INTERVIEW_REQUESTED: { label: 'Interview angefragt',   color: '#7C3AED', bgColor: 'rgba(124,58,237,0.12)' },
  INTERVIEW_SCHEDULED: { label: 'Interview vereinbart',  color: '#6366F1', bgColor: 'rgba(99,102,241,0.12)' },
  INTERVIEW_COMPLETED: { label: 'Interview durchgeführt', color: '#0EA5E9', bgColor: 'rgba(14,165,233,0.12)' },
  SECOND_INTERVIEW:    { label: 'Weiteres Interview',    color: '#0284C7', bgColor: 'rgba(2,132,199,0.12)' },
  OFFER:               { label: 'Angebot',               color: '#F59E0B', bgColor: 'rgba(245,158,11,0.12)' },
  OFFER_ACCEPTED:      { label: 'Angebot angenommen',    color: '#D97706', bgColor: 'rgba(217,119,6,0.12)' },
  PLACED:              { label: 'Vermittelt',            color: '#10B981', bgColor: 'rgba(16,185,129,0.12)' },
  REJECTED_CLIENT:     { label: 'Absage Kunde',          color: '#EF4444', bgColor: 'rgba(239,68,68,0.12)' },
  REJECTED_CANDIDATE:  { label: 'Absage Kandidat',       color: '#DC2626', bgColor: 'rgba(220,38,38,0.12)' },
  WITHDRAWN:           { label: 'Zurückgezogen',         color: '#9CA3AF', bgColor: 'rgba(156,163,175,0.12)' },
  CLOSED:              { label: 'Beendet',               color: '#6B7280', bgColor: 'rgba(107,114,128,0.12)' },
};

export type RejectionReason =
  | 'QUALIFICATION' | 'SALARY' | 'LOCATION' | 'AVAILABILITY'
  | 'CLIENT_DECISION' | 'CANDIDATE_DECISION' | 'POSITION_FILLED'
  | 'POSITION_CANCELLED' | 'OTHER';

export const REJECTION_LABEL: Record<RejectionReason, string> = {
  QUALIFICATION: 'Qualifikation',
  SALARY: 'Gehalt',
  LOCATION: 'Ort',
  AVAILABILITY: 'Verfügbarkeit',
  CLIENT_DECISION: 'Entscheidung des Kunden',
  CANDIDATE_DECISION: 'Entscheidung des Kandidaten',
  POSITION_FILLED: 'Stelle anderweitig besetzt',
  POSITION_CANCELLED: 'Stelle zurückgezogen',
  OTHER: 'Sonstiges',
};

export type InterviewType = 'PHONE' | 'VIDEO' | 'ONSITE' | 'OTHER';
export type InterviewStatus = 'PLANNED' | 'CONFIRMED' | 'COMPLETED' | 'CANCELLED' | 'NO_SHOW';

export const INTERVIEW_TYPE_LABEL: Record<InterviewType, string> = {
  PHONE: 'Telefon', VIDEO: 'Video', ONSITE: 'Vor Ort', OTHER: 'Sonstiges',
};

export const INTERVIEW_STATUS_LABEL: Record<InterviewStatus, { label: string; color: string }> = {
  PLANNED:   { label: 'Geplant',           color: '#6366F1' },
  CONFIRMED: { label: 'Bestätigt',         color: '#0EA5E9' },
  COMPLETED: { label: 'Durchgeführt',      color: '#10B981' },
  CANCELLED: { label: 'Abgesagt',          color: '#9CA3AF' },
  NO_SHOW:   { label: 'Nicht erschienen',  color: '#EF4444' },
};

export interface Submission {
  id: string;
  candidate_id: string;
  job_posting_id: string | null;
  client_company_id: string | null;
  client_contact_id: string | null;
  process_status: SubmissionStatus;
  statusLabel?: string;
  allowedTargets?: SubmissionStatus[];
  rejection_reason: RejectionReason | null;
  rejection_note: string | null;
  owner_user_id: string | null;
  submitted_at: string | null;
  offer_date: string | null;
  status_changed_at: string | null;
  created_at: string;
  job_posting?: { id: string; title: string; location: string | null; fee_percent: number | null } | null;
  client_company?: { id: string; name: string } | null;
  candidates?: { id: string; first_name: string; last_name: string; current_position: string | null } | null;
}

export interface Interview {
  id: string;
  submission_id: string;
  round_number: number;
  interview_type: InterviewType;
  interview_status: InterviewStatus;
  starts_at: string;
  duration_minutes: number | null;
  location: string | null;
  meeting_url: string | null;
  client_feedback: string | null;
  candidate_feedback: string | null;
  internal_notes: string | null;
}

export interface Placement {
  id: string;
  submission_id: string;
  start_date: string;
  annual_salary: number | null;
  fee_percent: number | null;
  fee_amount: number | null;
  status: string;
}

export interface VerlaufEintrag {
  id: string;
  from_process_status: string | null;
  to_process_status: string;
  changed_by: string | null;
  changed_at: string;
  source: string;
  reason: string | null;
  note: string | null;
}

export interface SubmissionDetail {
  submission: Submission;
  interviews: Interview[];
  placement: Placement | null;
  verlauf: VerlaufEintrag[];
}

async function fetchApi(url: string, options: RequestInit = {}) {
  const res = await fetch(url, {
    ...options,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...options.headers },
  });
  const json = await res.json();
  if (!res.ok || !json.success) {
    const fehler = new Error(json?.error || 'Anfrage fehlgeschlagen') as Error & { code?: string };
    fehler.code = json?.code;
    throw fehler;
  }
  return json.data;
}

const submissionWorkflowService = {
  /** Alle Gespräche der Organisation (Kalender-Agenda). */
  async alleInterviews(): Promise<ProzessInterview[]> {
    return fetchApi(`${API_BASE}/interviews`);
  },

  async jeKandidat(candidateId: string): Promise<Submission[]> {
    return fetchApi(`${API_BASE}/by-candidate/${candidateId}`);
  },

  async detail(submissionId: string): Promise<SubmissionDetail> {
    return fetchApi(`${API_BASE}/${submissionId}`);
  },

  async anlegen(params: { candidate_id: string; job_posting_id: string; client_contact_id?: string }): Promise<Submission> {
    return fetchApi(API_BASE, { method: 'POST', body: JSON.stringify(params) });
  },

  async setzeStatus(submissionId: string, status: SubmissionStatus, extra: { rejection_reason?: RejectionReason; note?: string } = {}) {
    return fetchApi(`${API_BASE}/${submissionId}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status, ...extra }),
    });
  },

  async interviewAnlegen(submissionId: string, params: {
    scheduled_at: string; interview_type?: InterviewType; duration_minutes?: number | null;
    location?: string | null; meeting_url?: string | null;
  }) {
    return fetchApi(`${API_BASE}/${submissionId}/interviews`, {
      method: 'POST', body: JSON.stringify(params),
    });
  },

  async interviewAktualisieren(interviewId: string, felder: {
    starts_at?: string; duration_minutes?: number | null; location?: string | null;
    meeting_url?: string | null; internal_notes?: string | null;
  }) {
    return fetchApi(`${API_BASE}/interviews/${interviewId}`, {
      method: 'PATCH', body: JSON.stringify(felder),
    });
  },

  async interviewStatus(interviewId: string, status: InterviewStatus, felder: {
    client_feedback?: string; candidate_feedback?: string; internal_notes?: string;
  } = {}) {
    return fetchApi(`${API_BASE}/interviews/${interviewId}/status`, {
      method: 'PATCH', body: JSON.stringify({ status, ...felder }),
    });
  },

  async vermittlungAnlegen(submissionId: string, params: {
    start_date: string; annual_salary?: number | null; fee_percent?: number | null; fee_amount?: number | null;
  }): Promise<Placement> {
    return fetchApi(`${API_BASE}/${submissionId}/placement`, {
      method: 'POST', body: JSON.stringify(params),
    });
  },
};

export default submissionWorkflowService;
